/**
 * Turns a pile of miles into sentences a nine-year-old can picture.
 *
 * "238,599 miles" is a number. "Almost all the way to the moon" is something you can feel.
 * The home page shows one of these at a time under the distance stat and cycles through
 * them, so the same total keeps handing back something new.
 *
 * Every figure below is a real published measurement, sourced in its comment, because the
 * whole point of a comparison is that it is true. Nothing is pinned to today's total —
 * the multiples are recomputed from whatever the postcards currently add up to, and a
 * comparison stays silent until the collection has actually earned it.
 *
 * Keep every line to about 35 characters. The stat card gives the text 156px on a desktop
 * and 125px on a phone — measured, not guessed — which is two wrapped rows at 35 characters
 * and three at 40. The card reserves two rows, so a longer line makes it grow mid-rotation.
 */

/** A real-world distance the total can be measured against. */
type Comparison = {
  /** Length, height or depth of the thing, in miles. */
  miles: number;
  /**
   * How to say it once the total is a whole one or more. `s` makes a word plural when the
   * printed multiple is not exactly one, so nothing ever reads "1 times".
   */
  say: (multiple: string, s: (word: string) => string) => string;
  /** Said when the total is within a whisker of the first one but has not reached it. */
  almost?: string;
  /** Said just after the total passes one. The crossing is the story, not the multiple. */
  justPast?: string;
  /** Said just after the total passes two — there and back again. */
  justPastTwice?: string;
};

/** Close enough that "almost" is honest rather than generous. */
const ALMOST_FROM = 0.9;

/** How far past a milestone it still counts as having just happened. */
const JUST_PAST_BAND = 0.08;

/**
 * Ordered deliberately: the first line anyone sees is the first one the collection has
 * earned, and it is also the only line shown to a reader who has asked for no motion.
 * The moon leads because right now it is a photo finish — the total is 256 miles short.
 */
const COMPARISONS: Comparison[] = [
  {
    // Mean Earth–Moon distance, 238,855 mi (384,400 km).
    miles: 238_855,
    say: (n, s) => `${n} ${s("time")} to the moon`,
    almost: "almost all the way to the moon",
    justPast: "all the way to the moon 🌙",
    justPastTwice: "to the moon and back 🌙",
  },
  {
    // Earth's equatorial circumference, 24,901 mi (40,075 km).
    miles: 24_901,
    say: (n, s) => `around the world ${n} ${s("time")}`,
    almost: "almost all the way around the world",
    justPast: "once all the way around the world",
  },
  {
    // Mt Everest, 29,032 ft above sea level (2020 China–Nepal joint survey) = 5.4985 mi.
    miles: 5.4985,
    say: (n, s) => `${n} ${s("Mt Everest")}, stacked up`,
  },
  {
    // Challenger Deep, the deepest point of the Mariana Trench: 35,876 ft = 6.795 mi.
    miles: 6.795,
    say: (n, s) => `${n} ${s("dive")} to the deepest ocean`,
  },
  {
    // Earth's mean diameter, 7,918 mi (12,742 km) — straight through, not around.
    miles: 7_918,
    say: (n, s) => `${n} ${s("trip")} through the Earth's core`,
  },
  {
    // The Great Wall of China, 21,196 km (13,171 mi), China's 2012 national survey.
    miles: 13_171,
    say: (n, s) => `${n} ${s("Great Wall")} of China`,
  },
  {
    // Trans Canada Trail, the world's longest trail network: over 30,000 km as of 2026.
    miles: 18_641,
    say: (n, s) => `${n} ${s("Trans Canada Trail")}`,
  },
  {
    // American Discovery Trail, coast to coast, 6,800 mi across both its routes.
    miles: 6_800,
    say: (n, s) => `${n} ${s("walk")} across America`,
  },
  {
    // Pacific Crest Trail, Mexico to Canada, 2,650 mi.
    miles: 2_650,
    say: (n, s) => `${n} ${s("hike")} of the Pacific Crest Trail`,
  },
  {
    // The 1860 Pony Express route, St. Joseph to Sacramento, 1,966 mi.
    miles: 1_966,
    say: (n, s) => `${n} Pony Express ${s("ride")}`,
  },
  {
    // Denver to Walt Disney World, great-circle, 1,545 mi.
    miles: 1_545,
    say: (n, s) => `${n} ${s("flight")} to Disney World`,
  },
  {
    // One lap of a single level of the Mall of America, 0.57 mi.
    miles: 0.57,
    say: (n, s) => `${n} ${s("lap")} of the Mall of America`,
  },
];

/**
 * Prints a multiple at the precision a person would actually say it out loud: 9.6 times
 * around the world, but 43,000 Everests — nobody counts the last four hundred.
 */
export function formatMultiple(times: number) {
  if (times >= 10_000) return (Math.round(times / 1000) * 1000).toLocaleString("en-US");
  if (times >= 100) return Math.round(times).toLocaleString("en-US");
  if (times >= 10) return String(Math.round(times));
  const rounded = Math.round(times * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** True while `times` sits in the short window just after passing `milestone`. */
function justPassed(times: number, milestone: number) {
  return times >= milestone && times < milestone + JUST_PAST_BAND;
}

/**
 * Every comparison the collection has earned, in the order they should be shown.
 *
 * Returns an empty list when nothing has travelled yet, so the caller can fall back to
 * plain wording rather than printing a boast about zero miles.
 */
export function distanceTrivia(totalMiles: number): string[] {
  if (!(totalMiles > 0)) return [];

  const lines: string[] = [];
  for (const comparison of COMPARISONS) {
    const times = totalMiles / comparison.miles;

    if (comparison.almost && times >= ALMOST_FROM && times < 1) {
      lines.push(comparison.almost);
      continue;
    }
    if (comparison.justPast && justPassed(times, 1)) {
      lines.push(comparison.justPast);
      continue;
    }
    if (comparison.justPastTwice && justPassed(times, 2)) {
      lines.push(comparison.justPastTwice);
      continue;
    }
    if (times < 1) continue;

    const multiple = formatMultiple(times);
    const pluralize = (word: string) => (multiple === "1" ? word : `${word}s`);
    lines.push(comparison.say(multiple, pluralize));
  }

  return lines;
}
